import { supabase } from "@/lovable-demo/runtime/client";
import type { ProblemRecord, WorkflowStage } from "@/lovable-demo/lib/warranty/types";
import { assessStageReadiness } from "./readiness";
import type { EvidenceGovernanceInput } from "./types";
import { getSignedInActor, GovernanceWriteError } from "./audit";

/**
 * Evidence governance: source, owner, date, version/status, provenance — plus
 * the Evidence Snapshot at Decision.
 *
 * Evidence continues to be displayed from the record itself. This registers the
 * governance metadata alongside it without changing the on-screen experience.
 *
 * CONTROLLED GOVERNANCE EVENTS (Class A): `registerEvidence` (evidence behind a
 * gate) and `captureEvidenceSnapshot` are confirmed and throw on failure.
 * `logEvidence` is telemetry only.
 */

export async function registerEvidence(input: EvidenceGovernanceInput): Promise<void> {
  const actor = await getSignedInActor();
  const { error } = await supabase.from("evidence_items").upsert(
    {
      record_id: input.recordId,
      record_code: input.recordCode ?? "",
      stage_id: input.stageId ?? "",
      chapter: input.chapter ?? "workflow",
      evidence_ref: input.evidenceRef,
      name: input.name,
      evidence_type: input.evidenceType ?? "aggregate-analytics",
      source_system: input.sourceSystem ?? "synthetic-seed",
      source_reference: input.sourceReference ?? "",
      owner: input.owner ?? actor.label,
      owner_user_id: actor.id,
      evidence_date: input.evidenceDate ?? new Date().toISOString().slice(0, 10),
      version: input.version ?? "1.0",
      status: input.status ?? "attached",
      provenance: input.provenance ?? "Evidence registered against the governed record.",
      aggregate_only: true,
      summary: input.summary ?? "",
      updated_at: new Date().toISOString(),
    },
    { onConflict: "record_id,evidence_ref" },
  );
  if (error) {
    throw new GovernanceWriteError(error.message || "The evidence record could not be written.");
  }
}

/**
 * A controlled action was rolled back after the evidence row was written (for
 * example the audit write failed). The row is never deleted — it is marked so it
 * can never read as successfully attached. Best effort; never blocks the caller.
 */
export async function markEvidenceNotRecorded(
  recordId: string,
  evidenceRef: string,
): Promise<void> {
  try {
    await supabase
      .from("evidence_items")
      .update({
        status: "not-recorded",
        summary: "Attachment rolled back — the governance audit write did not confirm.",
        updated_at: new Date().toISOString(),
      })
      .eq("record_id", recordId)
      .eq("evidence_ref", evidenceRef);
  } catch {
    /* best effort */
  }
}

/** TELEMETRY ONLY (Class B). Non-blocking; failures are ignored. */
export function logEvidence(input: EvidenceGovernanceInput): void {
  void registerEvidence(input).catch(() => undefined);
}

interface SnapshotInput {
  record: ProblemRecord;
  stage: WorkflowStage | undefined;
  decisionId: string;
  recommendationId: string;
  verdict: string;
  auditEventId?: string | null;
}

/**
 * Immutable picture of the evidence that existed when the human authorised the
 * decision. Confirmed write — throws on failure.
 */
export async function captureEvidenceSnapshot(input: SnapshotInput): Promise<void> {
  const actor = await getSignedInActor();
  const readiness = assessStageReadiness(input.record, input.stage);
  const { error } = await supabase.from("evidence_snapshots").insert({
    record_id: input.record.id,
    record_code: input.record.code,
    stage_id: input.stage?.id ?? "",
    decision_id: input.decisionId,
    recommendation_id: input.recommendationId,
    decision_verdict: input.verdict,
    decided_by_user_id: actor.id,
    decided_by_label: actor.label,
    audit_event_id: input.auditEventId ?? null,
    evidence_count: readiness.evidenceCount,
    required_evidence_count: readiness.requiredEvidenceCount,
    gate_satisfied: readiness.gateSatisfied,
    readiness_score: readiness.score,
    readiness_status: readiness.status,
    snapshot: {
      stageName: input.stage?.name ?? "",
      stageStatus: input.stage?.status ?? "",
      requiredEvidence: input.stage?.requiredEvidence ?? [],
      evidence: (input.stage?.evidence ?? []).map((e) => ({
        id: e.id,
        name: e.name,
        type: e.type,
        addedAt: e.addedAt,
        addedBy: e.addedBy,
        summary: e.summary,
      })),
      findings: readiness.findings,
      recordStatus: input.record.status,
      risk: input.record.risk,
      riskScore: input.record.riskScore,
      exposure: input.record.financial.exposure,
      actualCost: input.record.financial.actualCost,
    } as never,
  });
  if (error) {
    throw new GovernanceWriteError(
      error.message || "The Evidence Snapshot at Decision could not be written.",
    );
  }
}

export async function listEvidenceGovernance(recordId: string) {
  const { data } = await supabase
    .from("evidence_items")
    .select("*")
    .eq("record_id", recordId)
    .order("created_at", { ascending: false });
  return data ?? [];
}

export async function listEvidenceSnapshots(recordId: string) {
  const { data } = await supabase
    .from("evidence_snapshots")
    .select("*")
    .eq("record_id", recordId)
    .order("taken_at", { ascending: false });
  return data ?? [];
}
